import React from 'react';
import { MapPin, ClipboardList } from 'lucide-react';
import { format } from 'date-fns';

const ACTIVITY_COLORS = {
  patrolling:         { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Patrol'       },
  emergency_response: { bg: 'bg-red-100',     text: 'text-red-700',     label: 'Emergency'    },
  access_control:     { bg: 'bg-blue-100',    text: 'text-blue-700',    label: 'Access'       },
  surveillance:       { bg: 'bg-purple-100',  text: 'text-purple-700',  label: 'Surveillance' },
  inspection:         { bg: 'bg-amber-100',   text: 'text-amber-700',   label: 'Inspection'   },
  incident:           { bg: 'bg-red-100',     text: 'text-red-700',     label: 'Incident'     },
  'check-in':         { bg: 'bg-orange-100',  text: 'text-orange-700',  label: 'Check-in'     },
  'check-out':        { bg: 'bg-orange-100',  text: 'text-orange-700',  label: 'Check-out'    },
  clear:              { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Clear'        },
  break:              { bg: 'bg-gray-100',    text: 'text-gray-600',    label: 'Break'        },
  administrative:     { bg: 'bg-sky-100',     text: 'text-sky-700',     label: 'Admin'        },
  other:              { bg: 'bg-gray-100',    text: 'text-gray-600',    label: 'Other'        },
};

function fmtRelative(d) {
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  try { return format(new Date(d), 'MMM d, h:mm a'); } catch { return '—'; }
}

/**
 * ActivityLogsTab — purely presentational. Zero fetching.
 *
 * Props:
 *   activityLogs[]  — from shiftDetail.activityLogs (fetched by CustomerJobDetail)
 *
 * Scoped to the currently selected shift. Data already arrives via props from
 * getCustomerShiftDetail, so no independent service call is needed.
 */
const ActivityLogsTab = ({ activityLogs = [] }) => {
  if (activityLogs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <ClipboardList size={28} className="mb-2 opacity-30" />
        <p className="text-sm">No activity logged for this shift yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {activityLogs.map((log, i) => {
        const type = log.activity_type || log.status || 'other';
        const cfg = ACTIVITY_COLORS[type] || ACTIVITY_COLORS.other;
        const officerName = log.user_id
          ? `${log.user_id.firstName} ${log.user_id.lastName}`
          : 'Officer';

        return (
          <div key={log._id || i} className="flex items-start gap-2.5 rounded-lg border border-border bg-card p-3">
            <span className={`mt-0.5 flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${cfg.bg} ${cfg.text}`}>
              {cfg.label}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-foreground">{officerName}</p>
              {log.note && (
                <p className="text-xs text-muted-foreground line-clamp-3 mt-0.5">{log.note}</p>
              )}
              {log.location?.coordinates && (
                <a
                  href={`https://www.google.com/maps?q=${log.location.coordinates[1]},${log.location.coordinates[0]}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 flex items-center gap-1 text-[10px] text-[#CC457D] hover:underline"
                  onClick={e => e.stopPropagation()}
                >
                  <MapPin size={9} /> View on map
                </a>
              )}
              {log.presigned_photo_url && (
                <img
                  src={log.presigned_photo_url}
                  alt="Activity"
                  className="mt-2 h-16 w-16 rounded object-cover border border-border cursor-pointer"
                  onClick={() => window.open(log.presigned_photo_url, '_blank')}
                />
              )}
            </div>
            <span className="flex-shrink-0 text-[10px] text-muted-foreground/60 whitespace-nowrap">
              {fmtRelative(log.timestamp)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default ActivityLogsTab;
